import { useMemo } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { Badge } from '@/components/ui/badge';
import {
  ProfessionalAvatar,
  ProfessionalAvatarProps,
} from '@/components/ProfessionalAvatar';
import { Professional, Practice } from '@aldebaran/prisma';
import { getFullName } from '@/util/professional';
import { useMode } from '@/hooks/useMode';
import { getDemoLeadProfessional } from '@/util/demoPractice';
interface SelectableProfessionalProps extends ProfessionalAvatarProps {
  professional: Professional;
  practice?: Pick<
    Practice,
    'address' | 'onboardingDisplayMode' | 'name' | 'avatarId' | 'slug'
  > | null;
  selected?: boolean;
  selectable?: boolean;
  close?: boolean;
  onSelect: () => void;
  onClose?: () => void;
}

export const SelectableProfessional = ({
  professional,
  practice,
  onSelect,
  onClose,
  selected,
  close,
  selectable,
}: SelectableProfessionalProps) => {
  const showCloseButton = Boolean(selected && close && onClose);
  const isPracticeMode = practice?.onboardingDisplayMode === 'PRACTICE';
  const mode = useMode();

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose?.();
  };

  const avatarProfessional = isPracticeMode
    ? {
        firstname: '',
        lastname: '',
        title: '',
        avatarId: practice?.avatarId ?? null,
        gender: 'MALE' as const,
      }
    : professional;

  // Website demo: the placeholder doctor carries the lead's own identity.
  // Never in INVITATION mode
  const demoLead = useMemo(
    () =>
      mode === 'INVITATION' ? null : getDemoLeadProfessional(practice?.slug),
    [mode, practice?.slug],
  );

  const displayName = isPracticeMode
    ? practice?.name ?? ''
    : getFullName(
        demoLead
          ? {
              ...professional,
              firstname: demoLead.firstname,
              lastname: demoLead.lastname,
            }
          : professional,
      );

  const secondarySubtitleText = demoLead
    ? demoLead.medicalSpeciality
    : mode === 'INVITATION'
    ? practice?.address ?? null
    : professional.medicalSpeciality ?? null;

  const showSecondaryLine = Boolean(selected && secondarySubtitleText);
  const showReplacementBadge =
    !isPracticeMode && Boolean(professional.replacement);

  return (
    <div
      className="flex flex-col items-center justify-start gap-3.5 py-4"
      onClick={selectable ? onSelect : undefined}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="relative flex-shrink-0">
        <div
          className={`flex items-center justify-center overflow-hidden rounded-2.5xl bg-white p-2 w-[108px] h-[108px] n2xs:w-[148px] n2xs:h-[148px] ${
            showCloseButton || mode === 'PROFESSIONAL_LINK'
              ? ''
              : 'hover:bg-blue-50'
          } ${
            selected ? 'border border-indigo-800' : 'border border-transparent'
          }`}
        >
          <ProfessionalAvatar
            professional={avatarProfessional}
            className="w-[92px] h-[92px] n2xs:w-[132px] n2xs:h-[132px]"
            selectable={showCloseButton || selectable}
            selected={selected}
          />
        </div>
        {showCloseButton && (
          <button
            type="button"
            onClick={handleCloseClick}
            className="absolute -right-3 -top-3 z-1000 flex items-center justify-center cursor-pointer rounded-md bg-indigo-700 p-1 text-white transition-opacity hover:opacity-90"
            aria-label="Désélectionner le professionnel"
          >
            <RxCross2 className="size-4.5 shrink-0" />
          </button>
        )}
      </div>
      <div className="flex w-full max-w-42 flex-col items-center justify-start gap-1 text-center">
        <p className="text-md n2xl:text-lg font-semibold leading-tight text-indigo-900">
          {displayName}
        </p>
        {showSecondaryLine && (
          <p className="text-sm n2xl:text-base font-normal leading-tight text-gray-500">
            {secondarySubtitleText}
          </p>
        )}
        {showReplacementBadge && (
          <span className="min-h-4">
            <Badge variant="gray">
              {professional.gender === 'FEMALE' ? 'Remplaçante' : 'Remplaçant'}
            </Badge>
          </span>
        )}
        {!showSecondaryLine && !showReplacementBadge && <div className="h-4" />}
      </div>
    </div>
  );
};
